import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Send, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";

const contactSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().email("Please enter a valid email address").max(255, "Email must be less than 255 characters"),
  message: z.string().trim().min(10, "Message must be at least 10 characters").max(2000, "Message must be less than 2000 characters"),
});

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] as const },
  }),
};

const stagger = {
  visible: { transition: { staggerChildren: 0.12 } },
};

export const ContactForm = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = contactSchema.safeParse({ name, email, message });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from("contact_submissions")
        .insert({ name: result.data.name, email: result.data.email, message: result.data.message });

      if (error) throw error;

      setSubmitted(true);
      setName("");
      setEmail("");
      setMessage("");
    } catch (error) {
      console.error("Contact form error:", error);
      toast({
        title: "Error",
        description: "Failed to send your message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section className="px-6 py-24 md:py-32 border-t border-[hsl(0,0%,12%)]">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-4"
          >
            <CheckCircle className="w-12 h-12 text-[hsl(0,75%,50%)] mx-auto" />
            <h3
              className="text-2xl sm:text-3xl font-bold tracking-tight"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Message Sent
            </h3>
            <p className="text-[hsl(0,0%,55%)]">
              Thanks for reaching out. We'll get back to you soon.
            </p>
            <button
              onClick={() => setSubmitted(false)}
              className="text-sm tracking-widest uppercase text-[hsl(0,0%,40%)] hover:text-[hsl(0,75%,50%)] transition-colors duration-300 mt-4"
            >
              Send Another Message
            </button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section className="px-6 py-24 md:py-32 border-t border-[hsl(0,0%,12%)]">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
        >
          <motion.p
            variants={fadeUp}
            custom={0}
            className="text-xs tracking-[0.35em] uppercase text-[hsl(0,75%,50%)] font-medium mb-4 text-center"
          >
            Get In Touch
          </motion.p>
          <motion.h2
            variants={fadeUp}
            custom={1}
            className="text-3xl sm:text-5xl font-bold tracking-tight mb-4 text-center"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Contact Us
          </motion.h2>
          <motion.p
            variants={fadeUp}
            custom={2}
            className="text-[hsl(0,0%,55%)] text-base sm:text-lg leading-relaxed text-center mb-12 max-w-lg mx-auto"
          >
            Have a question, suggestion, or just want to say hello? Drop us a message.
          </motion.p>

          <motion.form
            variants={fadeUp}
            custom={3}
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-[hsl(0,0%,40%)] mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="w-full bg-transparent border border-[hsl(0,0%,18%)] px-4 py-3 text-sm text-[hsl(0,0%,92%)] placeholder:text-[hsl(0,0%,30%)] focus:border-[hsl(0,75%,50%)] focus:outline-none transition-colors duration-300"
                  maxLength={100}
                />
                {errors.name && (
                  <p className="text-xs text-[hsl(0,75%,50%)] mt-1">{errors.name}</p>
                )}
              </div>
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-[hsl(0,0%,40%)] mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full bg-transparent border border-[hsl(0,0%,18%)] px-4 py-3 text-sm text-[hsl(0,0%,92%)] placeholder:text-[hsl(0,0%,30%)] focus:border-[hsl(0,75%,50%)] focus:outline-none transition-colors duration-300"
                  maxLength={255}
                />
                {errors.email && (
                  <p className="text-xs text-[hsl(0,75%,50%)] mt-1">{errors.email}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs tracking-[0.2em] uppercase text-[hsl(0,0%,40%)] mb-2">
                Message
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What's on your mind?"
                rows={5}
                className="w-full bg-transparent border border-[hsl(0,0%,18%)] px-4 py-3 text-sm text-[hsl(0,0%,92%)] placeholder:text-[hsl(0,0%,30%)] focus:border-[hsl(0,75%,50%)] focus:outline-none transition-colors duration-300 resize-none"
                maxLength={2000}
              />
              {errors.message && (
                <p className="text-xs text-[hsl(0,75%,50%)] mt-1">{errors.message}</p>
              )}
              <p className="text-xs text-[hsl(0,0%,25%)] mt-1 text-right">
                {message.length}/2000
              </p>
            </div>

            <div className="text-center">
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 text-sm tracking-widest uppercase border border-[hsl(0,0%,25%)] px-10 py-4 hover:border-[hsl(0,75%,50%)] hover:text-[hsl(0,75%,50%)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Message
                  </>
                )}
              </button>
            </div>
          </motion.form>
        </motion.div>
      </div>
    </section>
  );
};
